import React from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, Minus, Plus, Trash2, ShoppingCart, ShoppingBag } from "lucide-react";
import { useCart, formatPrice } from "@/lib/cart";

export default function Cart() {
  const { items, updateQuantity, removeItem, totalCents, totalItems, clearCart } = useCart();
  const nav = useNavigate();

  if (items.length === 0) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 py-16 text-center">
          <ShoppingCart className="h-14 w-14 mx-auto text-[hsl(var(--muted-fg))] mb-4" />
          <h2 className="text-xl font-semibold text-[hsl(var(--fg))]" data-testid="cart-empty-title">
            Your cart is empty
          </h2>
          <p className="text-sm text-[hsl(var(--muted-fg))] mt-2">
            Browse our shop to find something you like!
          </p>
          <Link to="/shop">
            <Button className="mt-6 gap-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] min-h-[44px]" data-testid="cart-continue-shopping">
              <ShoppingBag className="h-4 w-4" /> Continue Shopping
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link
          to="/shop"
          className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-fg))] hover:text-[hsl(var(--fg))] transition-colors mb-6"
          data-testid="cart-back-link"
        >
          <ChevronLeft className="h-4 w-4" /> Continue Shopping
        </Link>

        <h1 className="text-2xl sm:text-3xl font-semibold text-[hsl(var(--fg))] mb-6" data-testid="cart-page-title">
          Shopping Cart ({totalItems})
        </h1>

        <div className="space-y-4">
          {items.map((item) => (
            <Card key={item.variationId} className="border border-[hsl(var(--border))]" data-testid={`cart-item-${item.variationId}`}>
              <CardContent className="p-4 flex items-center gap-4">
                {/* Image */}
                <div className="h-20 w-20 rounded-lg bg-[hsl(var(--secondary))] overflow-hidden shrink-0">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.productName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <ShoppingBag className="h-6 w-6 text-[hsl(var(--muted-fg))]" />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[hsl(var(--fg))] truncate">{item.productName}</h3>
                  {item.variationName && item.variationName !== "Regular" && (
                    <p className="text-xs text-[hsl(var(--muted-fg))]">{item.variationName}</p>
                  )}
                  <p className="text-sm font-medium mt-1">{formatPrice(item.priceCents, item.currency)}</p>
                </div>

                {/* Quantity */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => updateQuantity(item.variationId, item.quantity - 1)}
                    data-testid={`cart-qty-minus-${item.variationId}`}
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-6 text-center text-sm font-medium" data-testid={`cart-qty-${item.variationId}`}>
                    {item.quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => updateQuantity(item.variationId, item.quantity + 1)}
                    disabled={item.quantity >= 99}
                    data-testid={`cart-qty-plus-${item.variationId}`}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>

                {/* Subtotal + Remove */}
                <div className="text-right shrink-0">
                  <p className="font-semibold text-[hsl(var(--fg))]" data-testid={`cart-subtotal-${item.variationId}`}>
                    {formatPrice(item.priceCents * item.quantity, item.currency)}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]/80 mt-1 h-7 px-2"
                    onClick={() => removeItem(item.variationId)}
                    data-testid={`cart-remove-${item.variationId}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Summary */}
        <Card className="mt-6 border border-[hsl(var(--border))]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[hsl(var(--muted-fg))]">Subtotal ({totalItems} items)</span>
              <span className="text-lg font-bold text-[hsl(var(--fg))]" data-testid="cart-total">
                {formatPrice(totalCents)}
              </span>
            </div>
            <Separator className="mb-4" />
            <Button
              className="w-full min-h-[48px] text-base font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] hover:bg-[hsl(var(--primary))]/90 gap-2"
              onClick={() => nav("/shop/checkout")}
              data-testid="cart-checkout-button"
            >
              <ShoppingCart className="h-5 w-5" /> Proceed to Checkout
            </Button>
            <Button
              variant="ghost"
              className="w-full mt-2 text-sm text-[hsl(var(--muted-fg))] hover:text-[hsl(var(--destructive))]"
              onClick={clearCart}
              data-testid="cart-clear-button"
            >
              Clear Cart
            </Button>
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
